import { api } from './api';

export interface ActionItem {
  id: number;
  siteId?: number;
  tenantId?: number;
  pageId?: number;
  pageUrl?: string;
  actionType: string;
  category?: string;
  title: string;
  description?: string;
  rationale: string;
  whyItMatters?: string;
  severity: string;
  priority?: number;
  priorityScore: number;
  status: string;
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;
  dismissedReason?: string;
  dismissed_reason?: string;
  notes?: string;
  remarks?: string;
  evidence?: Record<string, unknown> | string;
  evidenceRaw?: string;
  evidenceData?: Record<string, unknown>;
  affectedCount?: number;
  affectedPages?: Array<Record<string, unknown>>;
  affectedScope?: string;
  sourceLabel?: string;
  sourceTable?: string;
  confidence?: string;
  condition?: string;
  scopeId?: string;
  checkId?: string;
  checkStatus?: string;
  checkSeverity?: string;
  sourceRef?: string;
  // Direct fields from ETL API (preferred)
  effortLevel?: 'LOW' | 'MEDIUM' | 'HIGH' | 'low' | 'medium' | 'high';
  expectedImpact?: 'LOW' | 'MEDIUM' | 'HIGH' | 'low' | 'medium' | 'high';
  estimatedMinutes?: number;
  isSafe?: boolean;
  relatedPillar?: string;
  relatedPageId?: number;
  // Legacy context object (deprecated, kept for backwards compatibility)
  context?: ActionContext;
  steps?: ActionStep[];
}

export interface ActionContext {
  effortLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  impactLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  whyItMatters: string;
  estimatedMinutes: number;
}

export interface ActionStep {
  order: number;
  description: string;
  completed?: boolean;
}

export interface ActionQueueStats {
  totalPending: number;
  totalCompleted: number;
  totalDismissed: number;
  completionRate: number;
  actionsCompletedThisWeek: number;
  actionsCompletedThisMonth: number;
}

export interface ActionUpdateRequest {
  status: 'PENDING' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED' | 'SNOOZED';
  reason?: string;
  dismissalReason?: string;
  snoozeDays?: number;
}

export interface ActionFilters {
  excludeLocalBusiness?: boolean;
  localBusinessOnly?: boolean;
  statuses?: string[];
}

/**
 * Get actions for a site (uses tenantId from site.organizationId)
 */
export const getActions = async (
  siteId: number, 
  tenantId?: number, 
  filters?: ActionFilters
): Promise<ActionItem[]> => {
  let url = `/earlystage/actions?siteId=${siteId}`;
  
  if (filters?.excludeLocalBusiness) {
    url += '&excludeLocalBusiness=true';
  }
  if (filters?.localBusinessOnly) {
    url += '&localBusinessOnly=true';
  }
  if (filters?.statuses?.length) {
    filters.statuses.forEach((status) => {
      url += `&status=${encodeURIComponent(status)}`;
    });
  }
  
  const response = await api.get<ActionItem[]>(url);
  return response.data;
};

/**
 * Update action status
 */
export const updateAction = async (
  _siteId: number,
  actionId: number,
  request: ActionUpdateRequest
): Promise<ActionItem> => {
  const response = await api.put<ActionItem>(
    `/earlystage/actions/${actionId}`,
    request
  );
  return response.data;
};

/**
 * Get action queue statistics
 */
export const getActionStats = async (siteId: number, tenantId?: number): Promise<ActionQueueStats> => {
  const response = await api.get<ActionQueueStats>(`/earlystage/actions/statistics?siteId=${siteId}`);
  return response.data;
};

/**
 * Regenerate actions from readiness assessment
 */
export const regenerateActions = async (siteId: number, tenantId?: number): Promise<void> => {
  await api.post(`/earlystage/actions/regenerate?siteId=${siteId}`);
};
